import React, { useState } from 'react';
import { MAX_CHANNEL_LABEL, suggestChannelId } from '../constants/channels';
import './ChannelForm.css';

export interface ChannelFormValues {
  label: string;
  /** '' racks the key on the Crews row, 'just' on the Just row */
  tag: 'just' | '';
}

interface ChannelFormProps {
  /** Rack a new channel, or rename / re-rack one an admin made */
  mode: 'create' | 'edit';
  initial?: ChannelFormValues;
  busy?: boolean;
  error?: string | null;
  onSubmit: (values: ChannelFormValues) => void;
  onCancel: () => void;
  /** Edit mode, created channels only — the built-ins stay */
  onDelete?: () => void;
}

/**
 * The channel admin's form, docked under the dial. A name and a row are all
 * a channel needs; its id (the chat room, the roster's handle) derives from
 * the name the same way the server derives it. Streamers are added
 * afterwards, in the list editor, one at a time.
 */
export const ChannelForm: React.FC<ChannelFormProps> = ({
  mode,
  initial,
  busy = false,
  error,
  onSubmit,
  onCancel,
  onDelete,
}) => {
  const [label, setLabel] = useState(initial?.label ?? '');
  const [tag, setTag] = useState<'just' | ''>(initial?.tag ?? '');
  const id = suggestChannelId(label);
  const valid = label.trim().length > 0 && id.length >= 2;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || busy) return;
    onSubmit({ label: label.trim(), tag });
  };

  return (
    <form className="channel-form" onSubmit={submit} aria-label={mode === 'create' ? 'New channel' : 'Edit channel'}>
      <div className="channel-form-title">{mode === 'create' ? 'New channel' : 'Edit channel'}</div>
      <div className="list-editor-row">
        <input
          type="text"
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder="Channel name"
          maxLength={MAX_CHANNEL_LABEL}
          autoFocus
          disabled={busy}
          className="list-editor-input"
          aria-label="Channel name"
        />
      </div>
      <div className="channel-form-row" role="group" aria-label="Row">
        <span className="channel-form-rail">Row</span>
        <button
          type="button"
          className="quick-chip"
          aria-pressed={tag === ''}
          onClick={() => setTag('')}
          disabled={busy}
          title="Sits with the crews"
        >
          Crews
        </button>
        <button
          type="button"
          className="quick-chip"
          aria-pressed={tag === 'just'}
          onClick={() => setTag('just')}
          disabled={busy}
          title="Sits on the Just row of topics"
        >
          Just
        </button>
      </div>
      {mode === 'create' && (
        <div className="channel-form-hint">
          {id.length >= 2 ? `Room and handle: ${id}` : 'Two or more letters or digits'}
        </div>
      )}
      {error && <div className="share-url error">{error}</div>}
      <div className="channel-form-actions">
        <button type="submit" className="list-editor-add" disabled={!valid || busy}>
          {busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
        </button>
        <button type="button" className="quick-chip" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        {onDelete && (
          <button
            type="button"
            className="quick-chip danger-chip"
            onClick={onDelete}
            disabled={busy}
            title="Remove this channel for everyone"
          >
            Delete channel
          </button>
        )}
      </div>
    </form>
  );
};
